import { useState, useEffect, useCallback } from "react";

const useVariantSelection = (
  variants,
  skuList,
  validVariants,
  onSelect,
  defaultItemCode
) => {
  const [selectedStrictVariant, setSelectedStrictVariant] = useState([]);
  const [selectedNonStrictVariant, setSelectedNonStrictVariant] =
    useState(null);

  const ifNonStrictVariantsValid = (currentSelectedNonStrictVariant) => {
    const nonStrictVariants = variants.filter((variant) => variant.nonStrict);
    return nonStrictVariants.length ? !!currentSelectedNonStrictVariant : true;
  };

  const ifSelectedItemCodeValid = (selectedItemCode) => {
    const strictVariants = variants.filter((variant) => !variant.nonStrict);
    return strictVariants.length ? typeof selectedItemCode === "string" : true;
  };

  useEffect(() => {
    if (!variants) return;
    let defaultNonStrictVariants = selectedNonStrictVariant;
    let defaultStrictVariants;

    if (defaultNonStrictVariants == null) {
      // getting default non strict variants
      const nonStrictVariants = variants.filter((variant) => variant.nonStrict);
      defaultNonStrictVariants = nonStrictVariants.reduce(
        (obj, variant) => ({
          ...obj,
          [variant.code]: variant?.values[0]?.value,
        }),
        {}
      );
    }

    if (!skuList || defaultItemCode == 0) {
      defaultStrictVariants = variants.map(() => 0);
    } else {
      // getting default strict variants
      const defaultProduct =
        defaultItemCode && skuList
          ? skuList.find((product) => product.itemCode === defaultItemCode)
          : null;

      if (!defaultProduct) {
        defaultStrictVariants = variants.map(() => 0);
      } else {
        defaultStrictVariants = variants.map((variant) => {
          const index = variant.values.findIndex(
            (value) => value.value === defaultProduct.variants[variant.code]
          );
          return index >= 0 ? index : undefined;
        });
      }
    }

    setSelectedNonStrictVariant((val) =>
      val ? val : defaultNonStrictVariants
    );
    setSelectedStrictVariant(defaultStrictVariants);
    onSelectVariantSuccess(defaultStrictVariants, defaultNonStrictVariants);
  }, [variants, skuList, defaultItemCode]);

  const getValidValue = (obj, key, value) => {
    if (obj.key === key) return obj.values[value];
  };

  const getValidVariantObj = useCallback(
    (selectedVariant, level) => {
      if (typeof validVariants === "string") return validVariants;
      let obj = { ...validVariants };

      for (const i in [...Array(level).keys()]) {
        if (selectedVariant[i] == null) break;
        const value = variants[i]?.values[selectedVariant[i]]?.value;
        let temp = getValidValue(obj, variants[i]?.code, value);
        if (!temp) return null;
        obj = temp;
      }

      return obj;
    },
    [variants]
  );

  const getValueIndexByValue = useCallback(
    (variantIndex, value) =>
      variants[variantIndex].values.findIndex((v) => v.value === value),
    [variants]
  );

  const selectVariant = useCallback(
    (variantIndex, valueIndex, selectedVariantCurrent) => {
      if (variants[variantIndex]?.nonStrict) {
        const { code, values } = variants[variantIndex];
        const selectedValue = values[valueIndex].value;
        let currentSelectedNonStrictVariant;
        if (!selectedNonStrictVariant) {
          currentSelectedNonStrictVariant = {
            [code]: selectedValue,
          };
        } else {
          if (selectedNonStrictVariant[code] === selectedValue) return;
          currentSelectedNonStrictVariant = {
            ...selectedNonStrictVariant,
            [code]: selectedValue,
          };
        }

        onSelectVariantSuccess(
          selectedStrictVariant,
          currentSelectedNonStrictVariant
        );
        return setSelectedNonStrictVariant(currentSelectedNonStrictVariant);
      }

      const currentSelectedVariant = [
        ...(selectedVariantCurrent || selectedStrictVariant).slice(
          0,
          variantIndex
        ),
        valueIndex,
      ];
      const strictVariants = variants.filter((variant) => !variant.nonStrict);
      if (variantIndex !== strictVariants.length - 1) {
        const newVariantIndex = variantIndex + 1;
        const obj = getValidVariantObj(currentSelectedVariant, newVariantIndex);
        if (typeof obj !== "object") return;
        const newValueIndex = getValueIndexByValue(
          newVariantIndex,
          Object.keys(obj.values)[0]
        );
        selectVariant(newVariantIndex, newValueIndex, currentSelectedVariant);
      } else {
        onSelectVariantSuccess(
          currentSelectedVariant,
          selectedNonStrictVariant
        );
        setSelectedStrictVariant(currentSelectedVariant);
      }
    },
    [variants, selectedNonStrictVariant, selectedStrictVariant]
  );

  const onSelectVariantSuccess = (
    currentSelectedVariant,
    currentSelectedNonStrictVariant
  ) => {
    if (!variants) return;
    if (!getValidVariantObj) return;

    const selectedItemCode = getValidVariantObj(
      currentSelectedVariant,
      variants.length
    );
    const isItemCodeValid = ifSelectedItemCodeValid(selectedItemCode);
    const isNonStrictVariantsValid = ifNonStrictVariantsValid(
      currentSelectedNonStrictVariant
    );
    if (isItemCodeValid && isNonStrictVariantsValid)
      onSelect(selectedItemCode, currentSelectedNonStrictVariant);
    else if (isNonStrictVariantsValid)
      onSelect(undefined, currentSelectedNonStrictVariant);

  };

  const isSelectedValue = (variantIndex, valueIndex) => {
    if (variants[variantIndex]?.nonStrict) {
      if (!selectedNonStrictVariant) return false;
      const { code, values } = variants[variantIndex];
      const selectedValue = values[valueIndex].value;

      return selectedNonStrictVariant[code] === selectedValue;
    }

    return valueIndex === selectedStrictVariant[variantIndex];
  };

  const isValidVariant = (variantIndex, value) => {
    if (variants[variantIndex]?.nonStrict) return true;
    const variantObj = getValidVariantObj(selectedStrictVariant, variantIndex);
    return (
      variantObj &&
      variantObj.values &&
      Object.keys(variantObj.values).includes(value)
    );
  };

  const getSelectedVariantValue = (index) => {
    const variant = variants[index];
    if (!variant) return null;

    if (variant.nonStrict)
      return selectedNonStrictVariant
        ? variant.values?.find(
          (value) => value.value === selectedNonStrictVariant?.[variant.code]
        )
        : variant.values[0];

    return selectedStrictVariant[index] != null
      ? variants?.[index]?.values?.[selectedStrictVariant[index]]
      : "";
  };

  return {
    selectedVariant: selectedStrictVariant,
    selectVariant,
    isValidVariant,
    isSelectedValue,
    getSelectedVariantValue,
  };
};

export default useVariantSelection;
